import { useMemo, useState } from "react";
import { CalendarClock, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtBRL, fmtInt, fmtPct } from "@/lib/format";
import { planPrice, planLucroLiquido } from "@/lib/plans";
import { useAuth, isVendedora } from "@/lib/auth";

const quarterOf = (isoOrYm) => {
  const s = (isoOrYm || "").slice(0, 7); // YYYY-MM
  if (!/^\d{4}-\d{2}/.test(s)) return null;
  const [y, m] = s.split("-");
  const q = Math.floor((parseInt(m, 10) - 1) / 3) + 1;
  return { y, q, label: `${y} · T${q}`, key: `${y}-Q${q}` };
};

const previousQuarter = (key) => {
  const [y, qStr] = key.split("-Q");
  let year = parseInt(y, 10);
  let q = parseInt(qStr, 10) - 1;
  if (q <= 0) { q = 4; year -= 1; }
  return `${year}-Q${q}`;
};

export default function MetasTrimestrais({ clientes = [], plans = [], commissionMap = {} }) {
  const { user } = useAuth();
  const showProfit = !isVendedora(user); // vendedora só vê receita/vendas
  const [metric, setMetric] = useState("receita"); // receita | lucro_bruto | lucro_liquido | vendas
  const activeMetric = showProfit ? metric : (metric === "vendas" ? "vendas" : "receita");

  // agrupa clientes por trimestre → totais globais e por vendedora
  const data = useMemo(() => {
    const map = new Map(); // key: {label, key, year, q, receita, lucro_bruto, comissao, vendas, porVendedor:{}}
    for (const c of clientes) {
      const q = quarterOf(c.created_at);
      if (!q) continue;
      const acc = map.get(q.key) || {
        ...q,
        receita: 0, lucro_bruto: 0, comissao: 0, vendas: 0,
        porVendedor: {},
      };
      const price = planPrice(c.plano_escolhido, plans);
      const lucro = planLucroLiquido(c.plano_escolhido, plans);
      const vend = (c.vendedor || "").trim() || "Sem vendedor";
      const pct = Number(commissionMap[vend] || 0);
      acc.receita += price;
      acc.lucro_bruto += lucro;
      acc.comissao += price * pct;
      acc.vendas += 1;
      const pv = acc.porVendedor[vend] || { receita: 0, lucro_bruto: 0, comissao: 0, vendas: 0 };
      pv.receita += price;
      pv.lucro_bruto += lucro;
      pv.comissao += price * pct;
      pv.vendas += 1;
      acc.porVendedor[vend] = pv;
      map.set(q.key, acc);
    }
    // ordena por key ascendente
    return [...map.values()]
      .map((r) => ({ ...r, lucro_liquido: r.lucro_bruto - r.comissao }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [clientes, plans, commissionMap]);

  // encontra deltas trimestre a trimestre para o metric selecionado
  const withDelta = useMemo(() => {
    const byKey = Object.fromEntries(data.map((r) => [r.key, r]));
    return data.map((r) => {
      const prev = byKey[previousQuarter(r.key)];
      const cur = Number(r[metric] || 0);
      const p = prev ? Number(prev[metric] || 0) : null;
      const delta = p === null ? null : cur - p;
      const deltaPct = p && p !== 0 ? ((cur - p) / Math.abs(p)) * 100 : null;
      return { ...r, _cur: cur, _prev: p, _delta: delta, _deltaPct: deltaPct };
    });
  }, [data, metric]);

  const latest = withDelta[withDelta.length - 1];
  const vendedoras = useMemo(() => {
    const s = new Set();
    for (const r of data) for (const v of Object.keys(r.porVendedor)) s.add(v);
    return [...s].sort();
  }, [data]);

  const METRIC_LABEL = {
    receita: "Receita bruta",
    lucro_bruto: "Lucro bruto",
    lucro_liquido: "Lucro líquido final",
    vendas: "Vendas",
  };
  const fmt = (v) => (metric === "vendas" ? fmtInt(v) : fmtBRL(v));

  const chartData = withDelta.map((r) => ({
    trimestre: r.label,
    valor: Number(r[metric] || 0),
    key: r.key,
  }));

  // barras coloridas conforme comparação com trimestre anterior
  const barColor = (idx) => {
    const item = withDelta[idx];
    if (!item || item._delta === null) return "hsl(var(--chart-3))";
    if (item._delta > 0) return "#10b981"; // verde
    if (item._delta < 0) return "#ef4444"; // vermelho
    return "hsl(var(--chart-3))";
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="metas-trimestrais">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Metas trimestrais</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Comparativo T vs T</Badge>
          <Select value={activeMetric} onValueChange={setMetric}>
            <SelectTrigger className="w-[210px]" data-testid="filter-metric-trimestre">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="receita">Receita bruta</SelectItem>
              {showProfit && <SelectItem value="lucro_bruto">Lucro bruto</SelectItem>}
              {showProfit && <SelectItem value="lucro_liquido">Lucro líquido final</SelectItem>}
              <SelectItem value="vendas">Vendas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Resumo do trimestre atual */}
      {latest && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Trimestre atual</div>
            <div className="font-heading text-lg font-bold mt-0.5">{latest.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {fmtInt(latest.vendas)} venda(s) · {Object.keys(latest.porVendedor).length} vendedora(s)
            </div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{METRIC_LABEL[metric]}</div>
            <div className="font-heading text-2xl font-extrabold text-primary tabular-nums mt-0.5">
              {fmt(latest._cur)}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">no trimestre corrente</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vs trimestre anterior</div>
            {latest._prev === null ? (
              <div className="text-sm text-muted-foreground mt-0.5">Sem trimestre anterior para comparar.</div>
            ) : (
              <>
                <div className="flex items-center gap-2 mt-0.5">
                  {latest._delta > 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  ) : latest._delta < 0 ? (
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  ) : (
                    <Minus className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div className={`font-heading text-xl font-extrabold tabular-nums ${
                    latest._delta > 0 ? "text-emerald-500" : latest._delta < 0 ? "text-red-500" : "text-muted-foreground"
                  }`}>
                    {latest._delta > 0 ? "+" : ""}{fmt(latest._delta)}
                  </div>
                  {latest._deltaPct !== null && (
                    <span className={`text-xs tabular-nums ${
                      latest._delta > 0 ? "text-emerald-500" : latest._delta < 0 ? "text-red-500" : "text-muted-foreground"
                    }`}>
                      ({latest._deltaPct > 0 ? "+" : ""}{fmtPct(latest._deltaPct)})
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Anterior: <span className="font-mono">{fmt(latest._prev)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Gráfico */}
      <div className="h-72" data-testid="trimestres-chart">
        {chartData.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-muted-foreground">
            Sem vendas suficientes para comparar trimestres.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="trimestre" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(v) => (metric === "vendas" ? fmtInt(v) : fmtBRL(v).replace(/\s/g, ""))}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(val) => [fmt(val), METRIC_LABEL[metric]]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="valor" name={METRIC_LABEL[metric]} radius={[8, 8, 0, 0]}>
                {chartData.map((row, idx) => (
                  <Cell key={row.label || row.name || `bar-${idx}`} fill={barColor(idx)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabela por vendedora */}
      {withDelta.length > 0 && vendedoras.length > 0 && (
        <div className="mt-6" data-testid="trimestres-vendedora">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h4 className="font-heading font-semibold">Por vendedora ({METRIC_LABEL[metric]})</h4>
          </div>
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left py-2 px-2">Vendedora</th>
                  {withDelta.slice(-4).map((r) => (
                    <th key={r.key} className="text-right py-2 px-2">{r.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendedoras.map((v) => (
                  <tr key={v} className="border-b border-border/50" data-testid={`row-trimestre-${v}`}>
                    <td className="py-2 px-2 font-medium">{v}</td>
                    {withDelta.slice(-4).map((r, idx) => {
                      const prev = idx > 0 ? withDelta.slice(-4)[idx - 1] : null;
                      const cur = Number(r.porVendedor[v]?.[metric === "lucro_liquido" ? "lucro_bruto" : metric] || 0);
                      const curFinal = metric === "lucro_liquido"
                        ? (Number(r.porVendedor[v]?.lucro_bruto || 0) - Number(r.porVendedor[v]?.comissao || 0))
                        : cur;
                      const prevVal = prev
                        ? (metric === "lucro_liquido"
                          ? (Number(prev.porVendedor[v]?.lucro_bruto || 0) - Number(prev.porVendedor[v]?.comissao || 0))
                          : Number(prev.porVendedor[v]?.[metric] || 0))
                        : null;
                      const up = prevVal !== null && curFinal > prevVal;
                      const down = prevVal !== null && curFinal < prevVal;
                      return (
                        <td key={r.key} className="text-right py-2 px-2 tabular-nums font-mono">
                          <span className={up ? "text-emerald-500" : down ? "text-red-500" : ""}>
                            {fmt(curFinal)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Cores das barras: <span className="text-emerald-500">verde</span> subiu vs. trimestre anterior,{" "}
            <span className="text-red-500">vermelho</span> caiu.
          </p>
        </div>
      )}
    </div>
  );
}
